import { Budget, Category, FinanceData, Transaction } from "./types";
import { shiftMonth, toDateKey } from "./format";

export const DEFAULT_CATEGORIES: Category[] = [
    { id: "salary", name: "Salary", type: "income", icon: "💼" },
    { id: "freelance", name: "Freelance", type: "income", icon: "🧑‍💻" },
    { id: "other-income", name: "Other income", type: "income", icon: "💰" },
    { id: "housing", name: "Housing", type: "expense", icon: "🏠" },
    { id: "groceries", name: "Groceries", type: "expense", icon: "🛒" },
    { id: "transport", name: "Transport", type: "expense", icon: "🚗" },
    { id: "utilities", name: "Utilities", type: "expense", icon: "💡" },
    { id: "dining", name: "Dining out", type: "expense", icon: "🍽️" },
    { id: "health", name: "Health", type: "expense", icon: "🩺" },
    { id: "entertainment", name: "Entertainment", type: "expense", icon: "🎬" },
    { id: "shopping", name: "Shopping", type: "expense", icon: "🛍️" },
];

const DEFAULT_BUDGETS: Budget[] = [
    { categoryId: "groceries", limit: 450_00 },
    { categoryId: "dining", limit: 75_00 },
    { categoryId: "transport", limit: 200_00 },
    { categoryId: "entertainment", limit: 30_00 },
    { categoryId: "shopping", limit: 250_00 },
];

// Recurring and occasional items: [categoryId, day of month, typical amount in cents, note]
const MONTHLY: [string, number, number, string][] = [
    ["salary", 1, 4200_00, "Monthly salary"],
    ["housing", 2, 1150_00, "Rent"],
    ["utilities", 6, 140_00, "Electricity & internet"],
    ["transport", 9, 65_00, "Fuel"],
    ["transport", 22, 58_00, "Fuel"],
    ["groceries", 4, 96_00, "Supermarket"],
    ["groceries", 11, 84_00, "Supermarket"],
    ["groceries", 18, 102_00, "Supermarket"],
    ["groceries", 25, 91_00, "Supermarket"],
    ["dining", 8, 42_00, "Dinner"],
    ["dining", 20, 36_00, "Lunch with friends"],
    ["entertainment", 14, 30_00, "Cinema & streaming"],
];
const OCCASIONAL: [string, number, number, string][] = [
    ["freelance", 15, 900_00, "Website project"],
    ["shopping", 12, 120_00, "Clothes"],
    ["health", 17, 75_00, "Pharmacy"],
    ["dining", 27, 64_00, "Restaurant"],
    ["other-income", 28, 150_00, "Sold old laptop"],
];

// Deterministic pseudo-random so the demo looks the same on every visit
function seeded(seed: number) {
    let s = seed;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

// Six months of realistic history ending today, so the demo is never stale
export function createSampleData(today = new Date()): FinanceData {
    const rand = seeded(7);
    const currentMonth = toDateKey(today).slice(0, 7);
    const transactions: Transaction[] = [];
    let id = 0;

    for (let offset = 5; offset >= 0; offset--) {
        const month = shiftMonth(currentMonth, -offset);
        const items = MONTHLY.concat(OCCASIONAL.filter((_, i) => (offset + i) % 2 === 0));
        for (let i = 0; i < items.length; i++) {
            const [categoryId, day, amount, note] = items[i];
            const date = `${month}-${String(day).padStart(2, "0")}`;
            // Only past dates in the current month
            if (offset === 0 && day > today.getDate()) {
                continue;
            }
            const variation = categoryId === "salary" || categoryId === "housing" ? 1 : 0.8 + rand() * 0.4;
            const category = DEFAULT_CATEGORIES.find(c => c.id === categoryId)!;
            transactions.push({
                id: `sample-${++id}`,
                type: category.type,
                amount: Math.round((amount * variation) / 100) * 100,
                categoryId,
                date,
                note,
            });
        }
    }

    return {
        version: 1,
        currency: "EUR",
        categories: DEFAULT_CATEGORIES,
        transactions,
        budgets: DEFAULT_BUDGETS,
    };
}
